import { Text } from "@ecco/ui/text";
import { Box } from "@ecco/ui/box";
import React from "react";
import { SummarySection } from "../SummarySection";
import { getStoreLines } from "@lib/utils/orderInfoUtility";
import { getDeliveryMethodKey } from "@lib/utils/findDeliveryMethods";
import { ShippingMethodTypes } from "@features/checkout/types";
import { useStore } from "@store/store";
import { useShippingMethods } from "@features/checkout/hooks/useShippingMethods";
import { useLocale } from "next-intl";
import { useEstimatedDeliveryDate, useExpectedDeliveryText } from "@hooks/useEstimatedDeliveryDate";
import {
    ESTIMATED_DELIVERY_HEADING,
    ESTIMATED_DELIVERY_HEADING_VB,
} from "@lib/utils/pdp/estimatedDelivery";
import ShoppingBag from "@lib/constants/contentful/shoppingBag";

type ShippingMethodSummaryProps = {
    methodId?: string;
};

export const ShippingMethodSummary = ({ methodId }: ShippingMethodSummaryProps) => {
    const getStoreData = useStore((state) => state.getStoreData);
    const locale = useLocale();
    const storeData = getStoreData();

    const { data: shippingMethods, isLoading } = useShippingMethods();

    const storeLines = getStoreLines(storeData);
    const clickAndCollectKey = getDeliveryMethodKey(ShippingMethodTypes.CLICK_AND_COLLECT);
    const isClickAndCollect = methodId === clickAndCollectKey;
    const selectedMethod = shippingMethods?.find((method) => method.key === methodId);
    const selectedMethodName = selectedMethod?.localizedName?.[locale] ?? selectedMethod?.name;

    const estimatedDeliveryDate = useEstimatedDeliveryDate();
    const showsExpectedDelivery =
        selectedMethod?.fulfillmentOption === "HD" || selectedMethod?.fulfillmentOption === "PS";
    const expectedDeliveryLabel = useExpectedDeliveryText(
        showsExpectedDelivery ? estimatedDeliveryDate : null
    );

    const expectedDeliveryLabelVB = useExpectedDeliveryText(
        showsExpectedDelivery ? estimatedDeliveryDate : null,
        ShoppingBag.expectedDeliveryVB
    );

    if (!methodId || isLoading) return null;

    const content = isClickAndCollect ? storeLines : expectedDeliveryLabel;
    const contentVB = isClickAndCollect ? storeLines : expectedDeliveryLabelVB;
    const renderContent = (value: React.ReactNode): React.ReactNode =>
        Array.isArray(value) ? (
            value.map((line: string) => (
                <Text
                    key={line}
                    fontSize={[
                        "mobileBodyTextSmall",
                        "mobileBodyTextSmall",
                        "desktopBodyTextSmall",
                    ]}
                >
                    {line}
                </Text>
            ))
        ) : (
            <Text>{value}</Text>
        );

    return (
        <SummarySection
            content={
                <>
                    <Box data-ab-id={ESTIMATED_DELIVERY_HEADING}>{renderContent(content)}</Box>
                    <Box data-ab-id={ESTIMATED_DELIVERY_HEADING_VB} className="ab-hide">
                        {renderContent(contentVB)}
                    </Box>
                </>
            }
            headline={<Text fontWeight="bold">{selectedMethodName}</Text>}
        />
    );
};
